import { FlavorDto } from "@juicy/models/dto";
import { useNavigation } from "@react-navigation/native";
import { useEffect, useRef, useState } from "react";
import { useToast } from "react-native-toast-notifications";
import { useRecoilState, useSetRecoilState } from "recoil";
import { apiService } from "../../App";
import { DataListType, useDataList, useUpdateDataList } from "../common/dataList/useDataList";
import { FlavorNavigation } from "../navigation";
import { ManufacturerSearchStringAtom, ManufacturerSelectedOptionAtom } from "./manufacturer/manufacturerAtoms";

export const useFlavor = (flavorId?: number) => {
    const navigation = useNavigation<FlavorNavigation>();
    const [selectedManufacturerOption, setSelectedManufacturerOption] = useRecoilState(ManufacturerSelectedOptionAtom);
    const setSearchString = useSetRecoilState(ManufacturerSearchStringAtom);
    const [isValid, setIsValid] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isFormPristine, setIsFormPristine] = useState(true);
    const { data, setData } = useDataList(DataListType.Flavor);

    const isFirstLoad = useRef(true);
    const flavorIdRef = useRef<number | undefined>(undefined);
    const [selectedFlavor, setSelectedFlavor] = useState<FlavorDto | null>(null);
    const [name, setName] = useState("");
    const [warning, setWarning] = useState("");
    const [comment, setComment] = useState("");
    const [rating, setRating] = useState<number | undefined>(undefined);
    const [inMixPercentage, setInMixPercentage] = useState("");
    const [singleFlavorPercentage, setSingleFlavorPercentage] = useState("");

    const { add } = useUpdateDataList(DataListType.Flavor);

    const toast = useToast();

    useEffect(() => {
        if (flavorId && isFirstLoad.current && isFirstLoad.current === true) {
            const getFlavorFromListData = (flavorId: number) => {
                return data.list.find((x) => x.id === flavorId);
            };
            const getFlavor = async () => {
                let flavor: FlavorDto | null = getFlavorFromListData(flavorId!);
                if (!flavor) {
                    setIsLoading(true);
                    flavor = await apiService.flavors.getOne(flavorId!);
                }
                navigation.setOptions({ title: `${flavor.manufacturer.shortname} ${flavor.name}` });
                setSelectedFlavor(flavor);
                setName(flavor.name);
                setRating(flavor.rating);
                setWarning(flavor.warning || "");
                setComment(flavor.comment || "");
                setInMixPercentage(flavor.inMixPercentage || "");
                setSingleFlavorPercentage(flavor.singleFlavorPercentage || "");
                setSelectedManufacturerOption({
                    label: flavor.manufacturer.shortname + " - " + flavor.manufacturer.longname,
                    value: flavor.manufacturer.id.toString(),
                    selected: true,
                });
                flavorIdRef.current = flavorId;
                setIsLoading(false);
            };
            if (flavorId && flavorId !== flavorIdRef.current) {
                void getFlavor();
            }
        }
    }, [flavorId, selectedManufacturerOption, setSelectedManufacturerOption, data.list, navigation]);

    useEffect(() => {
        if (isFirstLoad.current) {
            isFirstLoad.current = false;
            return;
        }
        setIsFormPristine(false);
        setIsValid(name.length > 0 && !!selectedManufacturerOption);
    }, [name, selectedManufacturerOption]);

    const save = async () => {
        if (isValid && selectedManufacturerOption) {
            setIsSaving(true);
            let successMessage = "Flavor created";
            let res: FlavorDto | null;
            if (selectedFlavor) {
                res = await apiService.flavors.edit({
                    ...selectedFlavor,
                    name,
                    rating,
                    warning,
                    manufacturerId: +selectedManufacturerOption?.value,
                    comment: comment,
                    singleFlavorPercentage,
                    inMixPercentage,
                });
                console.log(res);
                successMessage = "Flavor updated";
                updateDataList(res);
            } else {
                res = await apiService.flavors.create({
                    name,
                    manufacturerId: +selectedManufacturerOption?.value,
                    rating: rating,
                    warning: warning,
                    comment: comment,
                    inMixPercentage: inMixPercentage,
                    singleFlavorPercentage
                });
                add(res);
            }
            setSelectedFlavor(res);
            setSearchString("");
            toast.show(successMessage, {
                type: "success",
            });
            setIsSaving(false);
        }
    };

    const updateDataList = (flavorDto: FlavorDto) => {
        setData((prev) => {
            const newList: FlavorDto[] = [...prev.list];
            const index = newList.findIndex((x) => x.id === flavorDto.id);
            if (index !== -1) {
                newList[index] = flavorDto;
            }
            return { ...prev, list: newList };
        });
    };

    const saveAndGoBack = async () => {
        await save();
        if (navigation.canGoBack()) {
            navigation.goBack();
        } else {
            navigation.navigate("FlavorListScreen");
        }
    };

    const saveAndCreateAnother = async () => {
        await save();
        setName("");
        setSelectedManufacturerOption(null);
    };

    const saveAndGoToList = async () => {
        await save();
        setTimeout(() => {
            navigation.navigate("FlavorListScreen");
        }, 1);
    };

    return {
        saveAndCreateAnother,
        saveAndGoBack,
        isValid,
        isSaving,
        name,
        rating,
        warning,
        comment,
        inMixPercentage,
        singleFlavorPercentage,
        manufacturer: selectedFlavor?.manufacturer,
        setName,
        setRating,
        setWarning,
        setComment,
        setInMixPercentage,
        setSingleFlavorPercentage,
        isLoading,
        isFormPristine,
        saveAndGoToList,
    };

};
